/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay } from '@features/agenda/utils/agenda-dates';
import { isAllDayEvent, nowOffset } from './time-grid';
import { HOURS, SLOT_HEIGHT, WeekColumn, isToday } from './WeekColumn';
import { ALL_DAY_SCROLL, AllDayChip, AllDayLabel } from './AllDayBand';
import { Text } from '@/shared/ui';

interface WeekViewProps {
  days: Date[];
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
  onSlotClick?: (date: Date) => void;
}

/**
 * Seven-column week view with an hour grid and positioned events.
 *
 * @param props Week days, events, calendars and select handler.
 * @returns Rendered week view.
 */
export const WeekView: React.FC<WeekViewProps> = ({
  days,
  events,
  calendars,
  onSelect,
  onSlotClick,
}) => {
  const { t } = useTranslation('features/agenda');
  const cols = `52px repeat(${days.length}, 1fr)`;
  // The all-day band rides with the sticky header instead of sitting at 00:00
  // inside the grid, so these events stay readable at any scroll position.
  const allDayEvents = useMemo(() => events.filter(isAllDayEvent), [events]);
  const allDayByDay = useMemo(
    () => days.map((d) => allDayEvents.filter((e) => isSameDay(e.start_at_utc, d))),
    [days, allDayEvents]
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const offset = nowOffset(new Date(), SLOT_HEIGHT);
    if (offset !== null && scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(0, offset - scrollRef.current.clientHeight / 3);
    }
  }, []);

  return (
    <div
      data-testid="agenda-week-view"
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--surface-primary)]"
    >
      {/*
       * Single scroll container — header and body share the same scrollable
       * width, so `1fr` columns are always calculated against the same
       * reference (scrollbar gutter included). This prevents the header/body
       * column misalignment that arises when they live in separate grids.
       */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        {/* ── Sticky header (day labels + all-day band) ── */}
        <div className="sticky top-0 z-20 border-b border-[var(--border-subtle)] bg-[var(--surface-primary)]">
          <div
            className="grid"
            style={{ gridTemplateColumns: cols }}
          >
            <div />
            {days.map((d) => {
              const today = isToday(d);
              return (
                <div
                  key={d.toISOString()}
                  className="relative border-r border-[var(--border-subtle)] py-(--space-2) last:border-r-0"
                  style={today ? { background: 'color-mix(in srgb, var(--seed-base) 4%, transparent)' } : undefined}
                >
                  <Text
                    className={`m-0 text-center text-xs text-[var(--text-tertiary)] ${
                      today ? '[font-weight:var(--font-bold)]' : '[font-weight:var(--font-semibold)]'
                    }`}
                  >
                    {t(`weekday.${d.getDay()}`)}
                  </Text>
                  <div className="mt-(--space-0-5) flex justify-center">
                    {today ? (
                      <Text component="span" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--seed-base)] text-xs [font-weight:var(--font-bold)] text-[var(--button-primary-text)]">
                        {d.getDate()}
                      </Text>
                    ) : (
                      <Text component="span" className="inline-flex h-6 w-6 items-center justify-center text-xs [font-weight:var(--font-medium)] text-[var(--text-primary)]">
                        {d.getDate()}
                      </Text>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {allDayEvents.length > 0 && (
            <div
              data-testid="agenda-week-all-day"
              className="grid border-t border-[var(--border-ultra-subtle)]"
              style={{ gridTemplateColumns: cols }}
            >
              <AllDayLabel className="pr-(--space-2)" />
              {days.map((d, i) => (
                <div
                  key={d.toISOString()}
                  className="border-r border-[var(--border-subtle)] p-(--space-1) last:border-r-0"
                  style={isToday(d) ? { background: 'color-mix(in srgb, var(--seed-base) 4%, transparent)' } : undefined}
                >
                  <div className={ALL_DAY_SCROLL}>
                    {allDayByDay[i].map((ev) => (
                      <AllDayChip
                        key={`${ev.event_id}-${ev.occurrence_start}`}
                        event={ev}
                        calendars={calendars}
                        onSelect={onSelect}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Body grid ── */}
        <div
          className="grid"
          style={{ gridTemplateColumns: cols }}
        >
          <div>
            {HOURS.map((h) => (
              <div
                key={h}
                className="border-b border-[var(--border-ultra-subtle)] px-(--space-2) pt-(--space-0-5) text-right text-xs text-[var(--text-tertiary)]"
                style={{ height: SLOT_HEIGHT }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {days.map((d) => (
            <WeekColumn
              key={d.toISOString()}
              day={d}
              events={events}
              calendars={calendars}
              onSelect={onSelect}
              onSlotClick={onSlotClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
